import { FC, ReactNode, MouseEvent } from "react"
import { Link } from "gatsby"
import { classifyLink } from "../../utils/link-handling/link-utils"
import { NewWindowIcon } from "./NewWindowIcon"

export interface UniversalLinkProps {
  to: string
  children: ReactNode
  className?: string
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  showNewWindowIcon?: boolean
  newWindowIconSize?: "xxsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge"
  // Allow passing through other link props
  [key: string]: any
}

/**
 * Universal Link component that handles internal/external links consistently
 * - Shows new window icons when appropriate
 * - Uses Gatsby Link for internal navigation
 * - Handles external links with proper target behavior
 */
export const UniversalLink: FC<UniversalLinkProps> = ({
  to,
  children,
  className = "",
  onClick,
  showNewWindowIcon = true,
  newWindowIconSize = "xxsmall",
  ...otherProps
}) => {
  const linkClassification = classifyLink(to)
  const isContactForm = typeof window !== "undefined" && window.location.href.includes("?form=")
  const openInNewTab = isContactForm ? true : linkClassification.shouldOpenInNewTab
  const showIcon = showNewWindowIcon && (isContactForm ? true :linkClassification.shouldShowNewWindowIcon)

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Call custom onClick if provided
    if (onClick) {
      onClick(event)
    }
  }

  // For help center links, use Gatsby Link (internal navigation)
  if (linkClassification.isHelpCenter && !isContactForm) {
    return (
      <Link
        to={to}
        className={className}
        onClick={handleClick}
        {...otherProps}
      >
        {children}
      </Link>
    )
  }

  // For all other links, use regular anchor tag (external navigation)
  return (
    <a
      href={to}
      className={className}
      target={openInNewTab ? "_blank" : "_self"}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
      onClick={handleClick}
      {...otherProps}
    >
      {children}
      {showIcon && <NewWindowIcon size={newWindowIconSize} />}
    </a>
  )
}

export default UniversalLink
